/**
 * Environment Configuration Utility
 * Centralized environment detection and configuration management
 * STRICT MODE: No hardcoded fallbacks - all required variables must be provided
 */

export interface EnvironmentConfig {
  apiUrl: string;
  wsUrl: string;
  environment: 'development' | 'production' | 'test';
  domain: string;
  protocol: 'http' | 'https';
  port: number;
  isDevelopment: boolean;
  isProduction: boolean;
  isLocalNetwork: boolean;
  debugMode: boolean;
}

/**
 * Validate required environment variables
 */
const validateEnvironmentVariables = (): { apiUrl: string; wsUrl: string } => {
  const apiUrl = process.env.REACT_APP_API_URL;
  const wsUrl = process.env.REACT_APP_WS_URL;

  if (!apiUrl) {
    throw new Error(
      'FATAL: REACT_APP_API_URL is not defined. ' +
        'For local development, create a .env.development file with REACT_APP_API_URL. ' +
        'For production builds, ensure Cloud Build Trigger has the correct substitution variables.'
    );
  }

  if (!wsUrl) {
    throw new Error(
      'FATAL: REACT_APP_WS_URL is not defined. ' +
        'For local development, create a .env.development file with REACT_APP_WS_URL. ' +
        'For production builds, ensure Cloud Build Trigger has the correct substitution variables.'
    );
  }

  return { apiUrl, wsUrl };
};

/**
 * Detect current environment configuration
 */
export const getEnvironmentConfig = (): EnvironmentConfig => {
  // Server-side rendering fallback - still validate environment variables
  if (typeof window === 'undefined') {
    const { apiUrl, wsUrl } = validateEnvironmentVariables();
    return {
      apiUrl,
      wsUrl,
      environment: 'development',
      domain: 'server-side-rendering',
      protocol: 'http',
      port: 3000,
      isDevelopment: true,
      isProduction: false,
      isLocalNetwork: false,
      debugMode: true,
    };
  }

  // Validate required environment variables first
  const { apiUrl, wsUrl } = validateEnvironmentVariables();

  const currentHost = window.location.host;
  const currentProtocol = window.location.protocol as 'http:' | 'https:';
  const hostWithoutPort = currentHost.split(':')[0];
  const port = parseInt(currentHost.split(':')[1] || '80');

  // Environment detection
  const isDevelopment = process.env.NODE_ENV === 'development';
  const isProduction = !isDevelopment;
  const isLocalNetwork = hostWithoutPort.includes('192.168.') || hostWithoutPort.includes('10.');
  // const isFirebaseHosting =
  //   hostWithoutPort.includes('.web.app') || hostWithoutPort.includes('.firebaseapp.com');
  // Note: Removed hardcoded localhost check to enforce strict environment variable usage

  return {
    apiUrl,
    wsUrl,
    environment: isDevelopment ? 'development' : 'production',
    domain: hostWithoutPort,
    protocol: currentProtocol.replace(':', '') as 'http' | 'https',
    port,
    isDevelopment,
    isProduction,
    isLocalNetwork,
    debugMode: isDevelopment && !isProduction,
  };
};

/**
 * Get current environment configuration (cached)
 */
let cachedConfig: EnvironmentConfig | null = null;
export const getCurrentConfig = (): EnvironmentConfig => {
  if (!cachedConfig) {
    cachedConfig = getEnvironmentConfig();
  }
  return cachedConfig;
};

/**
 * Log environment configuration for debugging
 */
export const logEnvironmentConfig = (): void => {
  // const config = getCurrentConfig();
  // Environment configuration logging removed for production
};

/**
 * Check if analytics should be enabled based on environment
 */
export const shouldEnableAnalytics = (): boolean => {
  const config = getCurrentConfig();
  // Disable analytics in test environment or when explicitly disabled
  return config.environment !== 'test' && process.env.REACT_APP_ANALYTICS_ENABLED !== 'false';
};

/**
 * Check if debug logging should be enabled
 */
export const shouldEnableDebugLogging = (): boolean => {
  const config = getCurrentConfig();
  return config.debugMode || process.env.REACT_APP_DEBUG_MODE === 'true';
};

/**
 * Reset cached configuration (useful for testing)
 */
export const resetEnvironmentConfig = (): void => {
  cachedConfig = null;
};
